import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { AdminSidebar } from "@/components/sidebars";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";

const fmtBytes = (b) => {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("es-MX") : "—";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [backups, setBackups] = useState([]);
  const [backupBusy, setBackupBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetText, setResetText] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [carencias, setCarencias] = useState(null);

  const loadHealth = () => {
    apiClient.get("/admin/db/health").then(({ data }) => setHealth(data)).catch(() => {});
    apiClient.get("/admin/db/backups").then(({ data }) => setBackups(data)).catch(() => {});
  };

  useEffect(() => {
    apiClient.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    apiClient.get("/admin/carencias-stats").then(({ data }) => setCarencias(data)).catch(() => {});
    loadHealth();
  }, []);

  const handleBackupNow = async () => {
    setBackupBusy(true);
    try {
      await apiClient.post("/admin/db/backup");
      toast.success("Backup creado correctamente");
      loadHealth();
    } catch (e) {
      toast.error("Error al crear backup");
    } finally {
      setBackupBusy(false);
    }
  };

  const handleDownload = (filename) => {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    window.open(`${apiBase}/api/admin/db/backups/${encodeURIComponent(filename)}`, "_blank");
  };

  const handleReset = async () => {
    setResetBusy(true);
    try {
      const { data } = await apiClient.post("/admin/reset-data", { confirm: "REINICIAR" });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Datos reiniciados (${total} registros borrados)`);
      setResetOpen(false);
      setResetText("");
      apiClient.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
      loadHealth();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reiniciar datos");
    } finally {
      setResetBusy(false);
    }
  };

  const connPct = health?.connections?.current && health?.connections?.available
    ? Math.round((health.connections.current / (health.connections.current + health.connections.available)) * 100)
    : 0;
  const connHealthy = connPct < 80;

  return (
    <AppLayout sidebar={AdminSidebar}>
      <header className="app-header"><h1>Panel de Administración</h1></header>

      <section className="cards-row">
        <div className="stat-card" data-testid="adm-stat-members"><span className="stat-label">Miembros totales</span><strong className="stat-value">{stats?.total_members ?? "—"}</strong></div>
        <div className="stat-card stat-ok" data-testid="adm-stat-active"><span className="stat-label">Membresías activas</span><strong className="stat-value">{stats?.active_members ?? "—"}</strong></div>
        <div className="stat-card stat-warn" data-testid="adm-stat-pending"><span className="stat-label">Solicitudes pendientes</span><strong className="stat-value">{stats?.pending_solicitudes ?? "—"}</strong></div>
        <div className="stat-card" data-testid="adm-stat-camino"><span className="stat-label">En camino</span><strong className="stat-value">{stats?.en_camino ?? "—"}</strong></div>
        <div className="stat-card" data-testid="adm-stat-paid"><span className="stat-label">Pagos confirmados</span><strong className="stat-value">{stats?.total_paid_transactions ?? "—"}</strong></div>
      </section>

      {/* ===== CARENCIAS EN CURSO ===== */}
      <div className="panel" data-testid="carencias-panel">
        <h2 style={{ margin: "0 0 6px", display: "flex", alignItems: "center", gap: 10 }}>
          <i className="fas fa-hourglass-half" style={{ color: "#F59E0B" }} />
          Miembros en periodo de espera (carencias)
          <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
            {carencias ? `— ${carencias.total_active_members} miembros activos` : ""}
          </span>
        </h2>
        {!carencias ? (
          <p className="text-muted">Cargando…</p>
        ) : carencias.total_active_members === 0 ? (
          <p className="text-muted">No hay miembros activos en este momento.</p>
        ) : (
          <table className="data-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Servicio</th>
                <th>Carencia</th>
                <th>En espera</th>
                <th>Disponible</th>
                <th>% listos</th>
              </tr>
            </thead>
            <tbody>
              {carencias.por_servicio.map((row) => {
                const pct = carencias.total_active_members
                  ? Math.round((row.disponible / carencias.total_active_members) * 100)
                  : 0;
                return (
                  <tr key={row.key} data-testid={`carencia-row-${row.key}`}>
                    <td><strong>{row.label}</strong></td>
                    <td>{row.dias === 0 ? "Sin carencia" : `${row.dias} días`}</td>
                    <td>
                      {row.en_carencia > 0 ? (
                        <span style={{ background: "#FEF3C7", color: "#92400E", padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700 }}>
                          {row.en_carencia}
                        </span>
                      ) : (
                        <span className="text-muted">0</span>
                      )}
                    </td>
                    <td>
                      <span style={{ background: "#D1FAE5", color: "#065F46", padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700 }}>
                        {row.disponible}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: "#E5E7EB", borderRadius: 999, overflow: "hidden", minWidth: 80 }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: pct >= 80 ? "#22C55E" : pct >= 50 ? "#F59E0B" : "#EF4444" }} />
                        </div>
                        <small style={{ minWidth: 40, textAlign: "right" }}>{pct}%</small>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ===== DATABASE HEALTH ===== */}
      <div className="panel" data-testid="db-health-panel">
        <h2 style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 0 }}>
          <i className="fas fa-database" style={{ color: "#163680" }} />
          Salud de la base de datos
        </h2>

        {!health ? <p className="text-muted">Cargando…</p> : (
          <>
            <div className="cards-row" style={{ marginTop: 14 }}>
              <div className="stat-card">
                <span className="stat-label">Documentos</span>
                <strong className="stat-value">{health.documents_total?.toLocaleString("es-MX") ?? "—"}</strong>
                <small>en {health.collections} colecciones</small>
              </div>
              <div className="stat-card">
                <span className="stat-label">Tamaño datos</span>
                <strong className="stat-value" style={{ fontSize: 22 }}>{fmtBytes(health.data_size_bytes)}</strong>
                <small>+ {fmtBytes(health.indexes_size_bytes)} en índices</small>
              </div>
              <div className={`stat-card ${connHealthy ? "stat-ok" : "stat-warn"}`}>
                <span className="stat-label">Conexiones</span>
                <strong className="stat-value">{health.connections?.current}<small style={{ fontSize: 13, color: "#9ca3af" }}>/{health.connections?.current + (health.connections?.available || 0)}</small></strong>
                <small>{connPct}% en uso</small>
              </div>
              <div className="stat-card">
                <span className="stat-label">Uptime</span>
                <strong className="stat-value" style={{ fontSize: 20 }}>
                  {health.uptime_seconds ? Math.floor(health.uptime_seconds / 3600) + "h" : "—"}
                </strong>
                <small>desde último reinicio</small>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ===== BACKUPS ===== */}
      <div className="panel" data-testid="db-backups-panel">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <i className="fas fa-archive" style={{ color: "#7DC242" }} />
            Backups
            <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>(retención: {health?.backups?.retention_days || 7} días, automáticos cada 24h)</span>
          </h2>
          <button onClick={handleBackupNow} disabled={backupBusy} className="btn btn-primary" data-testid="btn-backup-now">
            {backupBusy ? "Creando…" : <><i className="fas fa-download" /> Crear backup ahora</>}
          </button>
        </div>

        {backups.length === 0 ? (
          <p className="text-muted">Sin backups todavía. Crea el primero con el botón ↑</p>
        ) : (
          <table className="data-table">
            <thead><tr><th>Archivo</th><th>Tamaño</th><th>Creado</th><th></th></tr></thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.filename} data-testid={`backup-row-${b.filename}`}>
                  <td><code style={{ fontSize: 12 }}>{b.filename}</code></td>
                  <td>{fmtBytes(b.size_bytes)}</td>
                  <td><small>{fmtDate(b.created_at)}</small></td>
                  <td>
                    <button onClick={() => handleDownload(b.filename)} className="btn-mini" data-testid={`download-${b.filename}`}>
                      <i className="fas fa-download" /> Descargar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ===== DANGER ZONE ===== */}
      <div className="panel" data-testid="danger-zone-panel" style={{ border: "1px solid #FCA5A5", background: "#FEF2F2" }}>
        <h2 style={{ margin: "0 0 6px", display: "flex", alignItems: "center", gap: 10, color: "#B91C1C" }}>
          <i className="fas fa-triangle-exclamation" />
          Zona de peligro
        </h2>
        <p className="text-muted" style={{ marginTop: 0 }}>
          Borra <strong>todos los clientes y sus datos</strong> (solicitudes, pagos, evaluaciones, referidos) de esta marca.
          Tu cuenta de administrador NO se borra. Esta acción no se puede deshacer.
        </p>
        {!resetOpen ? (
          <button onClick={() => setResetOpen(true)} className="btn" data-testid="btn-reset-open"
            style={{ background: "#B91C1C", color: "white" }}>
            <i className="fas fa-trash" /> Reiniciar datos
          </button>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 14 }}>Para confirmar, escribe <strong>REINICIAR</strong>:</span>
            <input
              type="text" value={resetText} onChange={(e) => setResetText(e.target.value)}
              placeholder="REINICIAR" data-testid="reset-confirm-input"
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14 }}
            />
            <button onClick={handleReset} disabled={resetText !== "REINICIAR" || resetBusy}
              className="btn" data-testid="btn-reset-confirm"
              style={{ background: resetText === "REINICIAR" ? "#B91C1C" : "#e5e7eb", color: resetText === "REINICIAR" ? "white" : "#9ca3af" }}>
              {resetBusy ? "Borrando…" : "Confirmar borrado"}
            </button>
            <button onClick={() => { setResetOpen(false); setResetText(""); }} className="btn btn-secondary" data-testid="btn-reset-cancel">
              Cancelar
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
